import {
  VehicleCatalogNodeKind,
  type CarFromLicensePlateRecordWithOwners,
  type VehicleCatalogNodeRecord
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { type SelectOption } from 'noddi-ui-web';
import { useState } from 'react';

import { VehicleCatalogNodeSelect } from '../VehicleCatalog/VehicleCatalogNodeSelect';
import { vehicleCatalogNodeOptionLabel } from '../VehicleCatalog/vehicleCatalogNodeOptions';

type NodeOption = SelectOption<number>;
type Props = {
  car: CarFromLicensePlateRecordWithOwners;
  onChange: (option: NodeOption | undefined) => void;
};

/** The catalog hierarchy, outermost first. Each level is scoped by the one above it. */
const LEVELS = [
  { kind: VehicleCatalogNodeKind.MAKE, label: 'Make' },
  { kind: VehicleCatalogNodeKind.MODEL, label: 'Model', disabledHint: 'Select a make first' },
  { kind: VehicleCatalogNodeKind.GENERATION, label: 'New catalog match', disabledHint: 'Select a model first' },
  {
    kind: VehicleCatalogNodeKind.MODIFICATION,
    label: 'Modification (optional)',
    disabledHint: 'Select a catalog match first'
  }
] as const;

const GENERATION_LEVEL = LEVELS.findIndex((level) => level.kind === VehicleCatalogNodeKind.GENERATION);
const MODIFICATION_LEVEL = LEVELS.findIndex((level) => level.kind === VehicleCatalogNodeKind.MODIFICATION);

/** A choice per level, by index. A level with no entry is untouched, so its seed still shows. */
type Choices = Record<number, NodeOption | undefined>;

/**
 * Picks a catalog match by walking the hierarchy the car already sits in: a flat list of every node
 * in the catalog offers thousands of rows that look alike, so each level is filtered by the one
 * above it, and the car's own ancestry seeds make and model.
 *
 * The match is the generation, which is what the signature links to; a modification narrows it to
 * one trim. Leaving the generation empty keeps the current match.
 */
export const VehicleCatalogMatchPicker = ({ car, onChange }: Props) => {
  const [choices, setChoices] = useState<Choices>({});
  const ancestor = (kind: VehicleCatalogNodeKind) => {
    const node = car.matchingDetails?.catalogNodes.find((candidate) => candidate.kind === kind);
    return node && seedOption(node);
  };

  // The registry always names the make and the model, so both are prefilled: from the car's linked
  // ancestry when it has one, otherwise from the catalog node carrying that exact name.
  const chosen = (level: number): NodeOption | undefined => choices[level];
  const touched = (level: number) => Object.hasOwn(choices, level);
  const makeAncestor = ancestor(VehicleCatalogNodeKind.MAKE);
  const namedMake = useNamedNode({
    kind: VehicleCatalogNodeKind.MAKE,
    name: car.makeName,
    enabled: !touched(0) && makeAncestor == null
  });
  const make = touched(0) ? chosen(0) : (makeAncestor ?? namedMake);
  const modelAncestor = ancestor(VehicleCatalogNodeKind.MODEL);
  const namedModel = useNamedNode({
    kind: VehicleCatalogNodeKind.MODEL,
    name: car.model,
    parentId: make?.value,
    enabled: !touched(1) && modelAncestor == null
  });
  const seeds: Choices = { 0: makeAncestor ?? namedMake, 1: modelAncestor ?? namedModel };
  const selected = (level: number): NodeOption | undefined => (touched(level) ? chosen(level) : seeds[level]);

  /**
   * A level owns everything under it: changing it clears those choices, and with them the match the
   * form would submit. The narrower of the two match levels is what is submitted.
   */
  const select = (level: number, option: NodeOption | undefined) => {
    const next: Choices = { [level]: option };
    for (const [key, choice] of Object.entries(choices)) {
      if (Number(key) < level) {
        next[Number(key)] = choice;
      }
    }
    setChoices(next);
    onChange(next[MODIFICATION_LEVEL] ?? next[GENERATION_LEVEL]);
  };

  // The registry names only the two outer levels; below them a search would narrow to the wrong rows.
  const initialSearch = [car.makeName, car.model];

  return (
    <div className='flex flex-col gap-5'>
      {LEVELS.map((level, index) => {
        const parentId = index === 0 ? undefined : selected(index - 1)?.value;
        const waitingHint = 'disabledHint' in level ? level.disabledHint : undefined;
        return (
          <VehicleCatalogNodeSelect
            key={level.kind}
            label={level.label}
            kinds={[level.kind]}
            parentIds={parentId != null ? [parentId] : undefined}
            enabled={waitingHint == null || parentId != null}
            waitingHint={waitingHint}
            initialSearch={initialSearch[index]}
            selectedOption={selected(index)}
            onSelectionChange={(option) => select(index, option)}
            wrapperClassName='min-w-0'
            className='w-full min-w-0'
          />
        );
      })}
    </div>
  );
};

/**
 * The catalog node at one level whose name is the registry's, so a car detail page opens with the
 * make and the model it already shows. Only an exact name (ignoring case and spacing) counts: a
 * near miss would scope the levels under it to the wrong model without saying so.
 */
const useNamedNode = ({
  kind,
  name,
  parentId,
  enabled
}: {
  kind: VehicleCatalogNodeKind;
  name: string;
  parentId?: number;
  enabled: boolean;
}): NodeOption | undefined => {
  const wanted = normalizedName(name);
  const scoped = kind === VehicleCatalogNodeKind.MAKE || parentId != null;
  const { data } = noddiAsync.useGet({
    type: URLKeys.getVehicleCatalogNodes,
    input: {
      pageIndex: 0,
      // The search already narrows this to one name; a page is only big enough to hold its homonyms.
      pageSize: 20,
      search: name,
      kinds: [kind],
      ...(parentId != null && { parentIds: [parentId] })
    },
    queryConfig: { enabled: enabled && scoped && wanted.length > 0 }
  });
  const node = data?.results.find((candidate) => normalizedName(candidate.name) === wanted);
  return node && seedOption(node);
};

/** The seed a dropdown shows before its own list loads: the label plus the make logo when there is one. */
const seedOption = (
  node: Pick<VehicleCatalogNodeRecord, 'id' | 'name' | 'parentName' | 'yearFrom' | 'yearTo' | 'logo'>
): NodeOption => ({
  value: node.id,
  label: vehicleCatalogNodeOptionLabel(node),
  imageUrl: node.logo?.url ?? undefined
});

const normalizedName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');
